import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';

// Components
import { PrimaryButton } from '@/views/components/buttons';

// Redux Actions
import { addFundToPortfolioById, addStockToPortfolioById, addAltToPortfolioById } from '@/redux/modules/edit-portfolio';
import { Dispatch } from '@/redux/index';

// Types
import PortfolioHolding from '@/types/portfolio-holding';
import { CustomProxiesData } from '@/types/custom-proxies-state';
import CUSTOM_PROXY_ITEM from '@/types/custom-proxy-item';

// Utils
import { findCustomProxyByKey, isCustomProxiesEmpty } from '@/utils/custom-proxies';
import { FormattedMessage } from '@/utils/i18n';
import { transformCustomProxyToQuickSearchItem } from '@/utils/quick-search';
import { COMPARISON_ITEM_TYPES } from '@/utils/constants';

// Enums
import { MISSING_PRODUCT_STEP } from '@/enums/MissingProduct';
import { CUSTOM_PROXY_TYPE } from '@/enums/CustomProxy';
import ProxyRefDetails from '@/types/proxy-ref';
import CustomProxy from '@/types/custom-proxy';

type PortfolioEditorHoldingsTableReplaceActionProps = {
  portfolioHolding: PortfolioHolding;
  hasRestrictedFlow: boolean;
  customProxies: CustomProxiesData;
  isFocusedQuickSearch?: boolean;
  i18nBasePath: string;
  setMissingProductStep: (step: MISSING_PRODUCT_STEP) => void;
  setHoldingToReplace: (holdingToReplace?: PortfolioHolding) => void;
  setFocusedQuickSearchEditPorfolio: (focused?: boolean, enableOnlyProxies?: boolean) => void;
  setReplacementProxyItem: (replacementProxyItem?: CUSTOM_PROXY_ITEM) => void;
  disabled?: boolean;
};

const apiActionMap: Record<string, typeof addFundToPortfolioById> = {
  [COMPARISON_ITEM_TYPES.FUND]: addFundToPortfolioById,
  [COMPARISON_ITEM_TYPES.BENCHMARK]: addFundToPortfolioById,
  [COMPARISON_ITEM_TYPES.STOCK]: addStockToPortfolioById,
  [COMPARISON_ITEM_TYPES.ALT]: addAltToPortfolioById,
};

const PortfolioEditorHoldingsTableReplaceAction: React.FC<PortfolioEditorHoldingsTableReplaceActionProps> = ({
  portfolioHolding,
  hasRestrictedFlow,
  customProxies,
  isFocusedQuickSearch,
  i18nBasePath,
  setMissingProductStep,
  setHoldingToReplace,
  setFocusedQuickSearchEditPorfolio,
  setReplacementProxyItem,
  disabled,
}) => {
  const dispatch = useDispatch() as Dispatch;

  const handleSimulationFlow = useCallback(
    (proxy: CustomProxy) => {
      const transformedProxy = transformCustomProxyToQuickSearchItem(proxy);
      setMissingProductStep(MISSING_PRODUCT_STEP.OPEN_MODAL);
      setReplacementProxyItem(transformedProxy);
    },
    [setMissingProductStep, setReplacementProxyItem],
  );

  const handleProxyRefFlow = useCallback(
    (proxy: CustomProxy) => {
      const proxyRefDetails = proxy.details as ProxyRefDetails;
      if (!proxyRefDetails?.proxyRef) {
        return false;
      }

      const { proxyRef } = proxyRefDetails;
      const apiAction = apiActionMap[proxyRef.type];

      if (proxyRef.proxyId && apiAction) {
        dispatch(apiAction(proxyRef.proxyId, portfolioHolding));
        return true;
      }
      return false;
    },
    [dispatch, portfolioHolding],
  );

  const handleReplaceClick = useCallback(() => {
    setHoldingToReplace(portfolioHolding);

    const restrictedFlow = hasRestrictedFlow && portfolioHolding.proxyKey && !isCustomProxiesEmpty(customProxies);

    if (restrictedFlow) {
      const matchingProxy = findCustomProxyByKey(customProxies, portfolioHolding.proxyKey!);

      if (matchingProxy) {
        if (matchingProxy.type === CUSTOM_PROXY_TYPE.SIMULATION) {
          handleSimulationFlow(matchingProxy);
          return;
        }
        if (handleProxyRefFlow(matchingProxy)) {
          return;
        }
      }
    }

    setMissingProductStep(MISSING_PRODUCT_STEP.SEARCH);
    if (!isFocusedQuickSearch) {
      setFocusedQuickSearchEditPorfolio(true, hasRestrictedFlow);
    }
  }, [
    setHoldingToReplace,
    portfolioHolding,
    hasRestrictedFlow,
    customProxies,
    setMissingProductStep,
    isFocusedQuickSearch,
    handleProxyRefFlow,
    handleSimulationFlow,
    setFocusedQuickSearchEditPorfolio,
  ]);

  return (
    <div className="replace-button-wrapper">
      <PrimaryButton className="primary-v2" onClick={handleReplaceClick} disabled={disabled}>
        <FormattedMessage id={`${i18nBasePath}.MissingHoldings.Replace`} />
      </PrimaryButton>
    </div>
  );
};

export default PortfolioEditorHoldingsTableReplaceAction;
